import {
  BrightnessCurve,
  CanvasAspect,
  ClipFit,
  ClipMotion,
  ClipTransition,
  ColorGrade,
  DEFAULT_BRIGHTNESS_CURVE_SECONDS,
  EditorSegment,
  ExportSettings,
} from '../types/editor';

export function segmentEffectiveDuration(seg: EditorSegment): number {
  const speed = Number.isFinite(seg.speed) && seg.speed > 0 ? seg.speed : 1;
  const dur = Number.isFinite(seg.duration) && seg.duration > 0 ? seg.duration : 0;
  return dur / speed;
}

export function totalTimelineDuration(segments: EditorSegment[]): number {
  return segments.reduce((acc, seg) => acc + segmentEffectiveDuration(seg), 0);
}

export function findSegmentAtTime(
  segments: EditorSegment[],
  time: number,
): { index: number; segment: EditorSegment; startTime: number; localTime: number } | null {
  let acc = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const dur = segmentEffectiveDuration(seg);
    if (time >= acc && time < acc + dur) {
      return { index: i, segment: seg, startTime: acc, localTime: (time - acc) * (seg.speed || 1) };
    }
    acc += dur;
  }
  if (segments.length > 0 && time >= acc) {
    const last = segments[segments.length - 1];
    const dur = segmentEffectiveDuration(last);
    return {
      index: segments.length - 1,
      segment: last,
      startTime: acc - dur,
      localTime: last.duration,
    };
  }
  return segments[0]
    ? { index: 0, segment: segments[0], startTime: 0, localTime: 0 }
    : null;
}

/** Index to insert a new clip relative to the playhead (after the clip under it). */
export function insertIndexAtPlayhead(segments: EditorSegment[], time: number): number {
  if (segments.length === 0 || time <= 0) return 0;
  let acc = 0;
  for (let i = 0; i < segments.length; i++) {
    const dur = segmentEffectiveDuration(segments[i]);
    if (time < acc + dur) return i + 1;
    acc += dur;
  }
  return segments.length;
}

export function canvasSize(
  aspect: CanvasAspect,
  resolution: ExportSettings['resolution'],
): { w: number; h: number } {
  const long = resolution === '1080p' ? 1920 : 1280;
  const short = resolution === '1080p' ? 1080 : 720;
  if (aspect === '16:9') return { w: long, h: short };
  if (aspect === '9:16') return { w: short, h: long };
  if (aspect === '4:3') return { w: Math.round((short * 4) / 3), h: short };
  if (aspect === '3:4') return { w: short, h: Math.round((short * 4) / 3) };
  return { w: short, h: short };
}

export function evenDim(n: number): number {
  const v = Math.max(2, Math.round(n));
  return v % 2 ? v + 1 : v;
}

export function fitVideoFilter(w: number, h: number, fit: ClipFit = 'contain'): string {
  const W = evenDim(w);
  const H = evenDim(h);
  if (fit === 'cover') {
    return `scale=${W}:${H}:force_original_aspect_ratio=increase,crop=${W}:${H},setsar=1,fps=30`;
  }
  return (
    `scale=${W}:${H}:force_original_aspect_ratio=decrease,` +
    `pad=${W}:${H}:(ow-iw)/2:(oh-ih)/2:black,setsar=1,fps=30`
  );
}

/** Ken Burns / still motion. `durationSec` is the output length. */
export function kenBurnsFilter(
  w: number,
  h: number,
  durationSec: number,
  motion: ClipMotion,
  fit: ClipFit = 'cover',
): string {
  const W = evenDim(w);
  const H = evenDim(h);
  if (motion === 'none') return fitVideoFilter(W, H, fit);
  const frames = Math.max(2, Math.round(Math.max(0.2, durationSec) * 30));
  const zEnd = 1.16;
  const step = ((zEnd - 1) / frames).toFixed(6);
  let z = '1';
  let x = 'iw/2-(iw/zoom/2)';
  let y = 'ih/2-(ih/zoom/2)';
  if (motion === 'zoom-in') {
    z = `min(1+on*${step},${zEnd})`;
  } else if (motion === 'zoom-out') {
    z = `max(${zEnd}-on*${step},1)`;
  } else if (motion === 'pan-left') {
    z = '1.12';
    x = `(iw-iw/zoom)*(1-on/${frames})`;
  } else if (motion === 'pan-right') {
    z = '1.12';
    x = `(iw-iw/zoom)*on/${frames}`;
  }
  const srcW = W * 2;
  const srcH = H * 2;
  return (
    `scale=${srcW}:${srcH}:force_original_aspect_ratio=increase,` +
    `crop=${srcW}:${srcH},` +
    `zoompan=z='${z}':x='${x}':y='${y}':d=${frames}:s=${W}x${H}:fps=30,` +
    `setsar=1`
  );
}

export function colorGradeFilter(grade: ColorGrade = 'none'): string {
  if (grade === 'bw') return 'hue=s=0';
  if (grade !== 'cinematic') return '';
  return [
    'eq=contrast=1.08:saturation=0.88:gamma=0.97:brightness=0.015',
    "curves=all='0/0 0.26/0.22 0.5/0.5 0.76/0.80 1/0.97'",
    'colorbalance=rs=0.04:bs=-0.05:rh=0.05:bh=-0.04',
    'vignette=PI/5.5',
  ].join(',');
}

export function brightnessCurveSeconds(seconds?: number, duration = 0): number {
  const raw = Number.isFinite(seconds) && (seconds as number) > 0
    ? Number(seconds)
    : DEFAULT_BRIGHTNESS_CURVE_SECONDS;
  const max = duration > 0 ? Math.max(0.05, duration / 2) : raw;
  return Math.min(1.5, Math.max(0.15, Math.min(raw, max)));
}

/** Smoothstep 0→1 brightness at clip-local time (effective duration). */
export function brightnessAtLocalTime(
  localTime: number,
  duration: number,
  curve: BrightnessCurve = 'none',
  seconds?: number,
): number {
  if (curve === 'none' || duration <= 0) return 1;
  const d = brightnessCurveSeconds(seconds, duration);
  const ease = (t: number) => {
    const x = Math.min(1, Math.max(0, t));
    return x * x * (3 - 2 * x);
  };
  let value = 1;
  if ((curve === 'in' || curve === 'both') && localTime < d) {
    value = Math.min(value, ease(localTime / d));
  }
  if ((curve === 'out' || curve === 'both') && localTime > duration - d) {
    value = Math.min(value, ease((duration - localTime) / d));
  }
  return Math.max(0, Math.min(1, value));
}

export function previewClipBrightness(args: {
  localTime: number;
  duration: number;
  curve?: BrightnessCurve;
  curveSeconds?: number;
  fadeIn?: boolean;
  fadeOut?: boolean;
  fadeSeconds?: number;
}): number {
  const curveB = brightnessAtLocalTime(args.localTime, args.duration, args.curve || 'none', args.curveSeconds);
  const fadeCurve: BrightnessCurve = args.fadeIn && args.fadeOut ? 'both' : args.fadeIn ? 'in' : args.fadeOut ? 'out' : 'none';
  const fadeB = brightnessAtLocalTime(args.localTime, args.duration, fadeCurve, args.fadeSeconds);
  return Math.min(curveB, fadeB);
}

export function brightnessFadeFilters(
  duration: number,
  curve: BrightnessCurve = 'none',
  seconds?: number,
): string[] {
  if (curve === 'none' || duration <= 0) return [];
  const d = brightnessCurveSeconds(seconds, duration);
  const parts: string[] = [];
  if (curve === 'in' || curve === 'both') {
    parts.push(`fade=t=in:st=0:d=${d.toFixed(3)}`);
  }
  if (curve === 'out' || curve === 'both') {
    parts.push(`fade=t=out:st=${Math.max(0, duration - d).toFixed(3)}:d=${d.toFixed(3)}`);
  }
  return parts;
}

export function xfadeName(transition: ClipTransition): 'fade' | 'fadeblack' | null {
  if (transition === 'dissolve') return 'fade';
  if (transition === 'fade') return 'fadeblack';
  return null;
}

export async function fetchMediaBlob(url: string): Promise<Blob | null> {
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return blob.size > 0 ? blob : null;
  } catch {
    return null;
  }
}

/** Video file URLs must never go into <img> — the browser shows a broken-image icon. */
export function isVideoFileSrc(url?: string | null): boolean {
  const u = String(url || '').trim();
  if (!u) return false;
  if (u.startsWith('data:video/')) return true;
  return /\.(mp4|webm|mov|m4v|mkv|avi)(\?|#|$)/i.test(u);
}

export function isLikelyImageSrc(url?: string | null): boolean {
  const u = String(url || '').trim();
  if (!u || isVideoFileSrc(u)) return false;
  if (u.startsWith('data:image/')) return true;
  if (/\.(jpe?g|png|gif|webp|avif)(\?|#|$)/i.test(u)) return true;
  if (/^https?:\/\//i.test(u) && /thumbnail|\/thumbs?\//i.test(u)) return true;
  return false;
}

/** FFmpeg atempo only accepts 0.5–2.0; chain for other rates. */
export function atempoFilter(speed: number): string {
  const rate = Number.isFinite(speed) && speed > 0 ? speed : 1;
  if (Math.abs(rate - 1) < 0.01) return '';
  const parts: string[] = [];
  let s = rate;
  while (s > 2) {
    parts.push('atempo=2.0');
    s /= 2;
  }
  while (s < 0.5) {
    parts.push('atempo=0.5');
    s *= 2;
  }
  parts.push(`atempo=${s.toFixed(4)}`);
  return parts.join(',');
}

export function audioWriteName(blob: Blob, stem: string): string {
  const t = (blob.type || '').toLowerCase();
  if (t.includes('wav') || t.includes('wave')) return `${stem}.wav`;
  if (t.includes('ogg') || t.includes('opus')) return `${stem}.ogg`;
  if (t.includes('mp4') || t.includes('m4a') || t.includes('aac')) return `${stem}.m4a`;
  if (t.includes('mpeg') || t.includes('mp3')) return `${stem}.mp3`;
  return `${stem}.wav`;
}

/** First frame as a data URL. Uses the video element so we never need FFmpeg for the timeline poster. */
export function posterFromVideoBlob(blob: Blob, atSec = 0.08): Promise<string> {
  return new Promise((resolve) => {
    if (!blob || blob.size === 0) {
      resolve('');
      return;
    }
    const url = URL.createObjectURL(blob);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    let settled = false;
    const finish = (src: string) => {
      if (settled) return;
      settled = true;
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(src);
    };
    const grab = () => {
      try {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) {
          finish('');
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = 160;
        canvas.height = Math.max(1, Math.round((160 * h) / w));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          finish('');
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg', 0.72));
      } catch {
        finish('');
      }
    };
    video.onseeked = grab;
    video.onloadeddata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const t = duration > 0 ? Math.min(Math.max(0, atSec), Math.max(0, duration - 0.05)) : 0;
      try {
        if (duration > 0 && video.currentTime !== t) {
          video.currentTime = t;
          return;
        }
      } catch { /* grab current frame */ }
      grab();
    };
    video.onerror = () => finish('');
    window.setTimeout(() => finish(''), 8000);
    video.src = url;
  });
}

export function durationFromMediaUrl(url: string, isImage: boolean, fallback = 8): Promise<number> {
  if (isImage) return Promise.resolve(4);
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    let settled = false;
    const finish = (value: number) => {
      if (settled) return;
      settled = true;
      video.removeAttribute('src');
      video.load();
      resolve(value);
    };
    video.onloadedmetadata = () => {
      const d = video.duration;
      finish(Number.isFinite(d) && d > 0 ? d : fallback);
    };
    video.onerror = () => finish(fallback);
    window.setTimeout(() => finish(fallback), 8000);
    video.src = url;
  });
}

